/**
 * AssessmentLetterForm: the letter's input form (date, RE line, visit notes,
 * the picked recommendations, Generate). The client identity and the
 * recipients live in the page-level AssessmentClientBlock (the ONE client
 * block); the recommendation picks are page-owned too (the library card
 * below the form feeds them) and arrive as props so this form only lists
 * them and sends them.
 *
 * There is NO author field: name and credentials come from the
 * authenticated profile on the server, never from the form.
 *
 * "Write the letter" waits for a usable pronoun choice (all five forms of a
 * custom set; a choice at all for a bound client) and for any pick being
 * re-worded under a new choice. With no client and no choice the letter and
 * the picks use they/them, and the form says so.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { ASSESSMENT_LETTER_NOTES_DRAFT_PREFIX, readStoredDraft, writeStoredDraft } from '@/lib/draftAutosave';
import { useTenant } from '@/lib/auth';
import { aiGenerateKeyOf } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { Button } from '@/components/primitives/Button';
import {
  aiToolsApi,
  type ApiError,
  type AssessmentFormInputSnapshot,
  type AssessmentLetterDraftInput,
  type AssessmentLetterDraftResponse,
  type RecommendationCuration,
  type RecommendationRef,
  type SpicePronounChoice,
} from '@/lib/api';
import { isPickStale } from '@/lib/recommendationText';
import { ASSESSMENT_NOTES_MAX } from './AssessmentDraftForm';
import { pronounChoiceLabel } from './AssessmentClientBlock';
import { DateInput } from '@/components/primitives/DateInput';

/** A vetted recommendation the CM picked for the letter (page-owned). */
export interface LetterRecommendationPick {
  id: string;
  topic: string;
  text: string;
  // The source that lets the page re-word the text when the pronoun choice
  // changes (see useReresolvedPicks). None of it rides the letter request.
  ref?: RecommendationRef;
  curation?: RecommendationCuration;
  worded_with?: SpicePronounChoice;
  /** The choice key for which re-wording failed; the text is kept. */
  stale_for?: string;
}

export interface AssessmentLetterFormProps {
  hasDraft?: boolean;
  tenantSlug: string;
  /** From the page-level client block. */
  recipients: string;
  clientFirstName: string;
  clientLastName: string;
  pronouns?: SpicePronounChoice;
  /** False while a custom pronoun set is incomplete; Write waits. */
  pronounsComplete: boolean;
  /** True while a bound client needs a choice before Write. */
  pronounsRequired: boolean;
  /** True while a pick waits to be re-worded; Write waits. */
  picksPending: boolean;
  /** ISO date for the letter header (defaults to today on the page). */
  letterDate: string;
  onLetterDateChange: (v: string) => void;
  /** The RE line (defaults to the client's name until edited). */
  re: string;
  onReChange: (v: string) => void;
  picks: readonly LetterRecommendationPick[];
  onRemovePick: (id: string) => void;
  /** The pronoun choice key in effect; a pick whose re-wording failed for it
   *  shows a note and a Try again control (onRetryPick). */
  currentPronounKey: string;
  onRetryPick: (id: string) => void;
  onGeneratingChange?: (generating: boolean) => void;
  onDraftReady: (response: AssessmentLetterDraftResponse, formInput: AssessmentFormInputSnapshot) => void;
  /** The notes as kept (src/lib/keptWork.ts). */
  keptNotes?: string;
  /** Every change to the notes, so the page keeps the whole form. */
  onNotesChange?: (notes: string) => void;
}

const NOTES_MIN = 200;
const COUNTER_VISIBLE_FROM = 1500;

function notesDraftKey(tenantSlug: string): string {
  return `${ASSESSMENT_LETTER_NOTES_DRAFT_PREFIX}:${tenantSlug}`;
}

const INPUT_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-sm font-semibold text-sage-800';

export function AssessmentLetterForm({
  hasDraft = false,
  tenantSlug,
  recipients,
  clientFirstName,
  clientLastName,
  pronouns,
  pronounsComplete,
  pronounsRequired,
  picksPending,
  letterDate,
  onLetterDateChange,
  re,
  onReChange,
  picks,
  onRemovePick,
  currentPronounKey,
  onRetryPick,
  onGeneratingChange,
  onDraftReady,
  keptNotes,
  onNotesChange,
}: AssessmentLetterFormProps): ReactNode {
  // The page keeps the whole form, the notes included (onNotesChange,
  // src/lib/keptWork.ts). Notes typed before that, under the notes draft
  // key, are read once and moved over, stamped with and restored only to
  // the person signed in.
  const draftAuthor = useTenant().principal?.userId ?? null;
  const [notes, setNotes] = useState(() => keptNotes ?? readStoredDraft(notesDraftKey(tenantSlug), draftAuthor));
  useEffect(() => {
    if (draftAuthor !== null) writeStoredDraft(notesDraftKey(tenantSlug), '', null);
  }, [tenantSlug, draftAuthor]);
  useEffect(() => {
    onNotesChange?.(notes);
  }, [notes]);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const snapshot = (): AssessmentFormInputSnapshot => ({
    recipient: recipients.trim(),
    client_first_name: clientFirstName.trim(),
    client_last_name: clientLastName.trim(),
    notes: notes.trim(),
    assessment_date: letterDate.trim().length > 0 ? letterDate.trim() : undefined,
    // Every letter records a choice: an unset one is the they/them the server
    // resolves.
    pronouns: pronouns ?? { preset: 'they_them' },
  });

  const buildInput = (): AssessmentLetterDraftInput => ({
    client_first_name: clientFirstName.trim(),
    client_last_name: clientLastName.trim(),
    recipients: recipients.trim(),
    re: re.trim(),
    letter_date: letterDate.trim().length > 0 ? letterDate.trim() : undefined,
    notes: notes.trim(),
    ...(pronouns ? { pronouns } : {}),
    recommendation_picks: picks.map((p) => ({ topic: p.topic, text: p.text })),
  });

  // The request and its snapshot are built when Write is pressed and travel
  // as the mutation's variables. React Query syncs mutation options in an
  // effect, so a mutationFn reading this render's props can still hold the
  // previous render's picks when the click lands.
  const mutation = useMutation({
    mutationFn: (vars: {
      input: AssessmentLetterDraftInput;
      snapshot: AssessmentFormInputSnapshot;
    }) => aiToolsApi.draftAssessmentLetter(tenantSlug, vars.input),
    onSuccess: (res, vars) => {
      setErrorKey(null);
      onDraftReady(res, vars.snapshot);
    },
    onError: (e: ApiError) => {
      setErrorKey(aiGenerateKeyOf(e) ?? 'unknown');
    },
  });

  const [elapsed, setElapsed] = useState(0);
  const generating = mutation.isPending;
  useEffect(() => {
    onGeneratingChange?.(generating);
    if (!generating) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating, onGeneratingChange]);

  const notesShort = notes.trim().length > 0 && notes.trim().length < NOTES_MIN;
  const canSubmit =
    recipients.trim().length > 0 &&
    clientFirstName.trim().length > 0 &&
    clientLastName.trim().length > 0 &&
    re.trim().length > 0 &&
    notes.trim().length >= NOTES_MIN &&
    pronounsComplete &&
    (pronouns !== undefined || !pronounsRequired) &&
    !picksPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ input: buildInput(), snapshot: snapshot() });
      }}
      className="space-y-4"
      data-testid="assessment-letter-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="letter-date" className={LABEL_CLS}>
            Letter date
          </label>
          <DateInput
            id="letter-date"
            value={letterDate}
            onChange={(e) => onLetterDateChange(e.target.value)}
            disabled={generating}
            size="field" className="mt-1.5 block w-full"
            data-testid="assessment-letter-date"
          />
        </div>
        <div>
          <label htmlFor="letter-re" className={LABEL_CLS}>
            RE
          </label>
          <input
            id="letter-re"
            type="text"
            value={re}
            onChange={(e) => onReChange(e.target.value)}
            disabled={generating}
            placeholder="The client's name"
            className={INPUT_CLS}
            data-testid="assessment-letter-re"
          />
        </div>
      </div>

      <div>
        {!pronounsComplete ? (
          <p className="mb-2 text-xs text-ink-soft" data-testid="assessment-pronoun-hint">
            Fill in all five pronoun forms in the client block above, or choose a set.
          </p>
        ) : pronouns === undefined && pronounsRequired ? (
          <p className="mb-2 text-xs text-ink-soft" data-testid="assessment-pronoun-hint">
            Choose the client's pronouns in the client block above. The letter and the
            recommendations use them.
          </p>
        ) : pronouns === undefined ? (
          <p className="mb-2 text-xs text-ink-soft" data-testid="assessment-pronoun-default-note">
            The letter uses they and them until you choose pronouns.
          </p>
        ) : null}
        <label htmlFor="letter-notes" className={LABEL_CLS}>
          Visit notes, observations, or transcript
        </label>
        <textarea
          id="letter-notes"
          value={notes}
          maxLength={ASSESSMENT_NOTES_MAX}
          onChange={(e) => {
            const next = e.target.value;
            setNotes(next.length > ASSESSMENT_NOTES_MAX ? next.slice(0, ASSESSMENT_NOTES_MAX) : next);
          }}
          rows={10}
          disabled={generating}
          aria-describedby="assessment-letter-notes-counter"
          placeholder="Paste anything you've got from the visit: what you observed, what you talked through, the family's questions, your own follow-up thoughts. The letter follows your notes."
          className={INPUT_CLS}
          data-testid="assessment-letter-notes"
        />
        <p
          id="assessment-letter-notes-counter"
          className="mt-1.5 text-xs text-ink-soft"
          data-testid="assessment-letter-notes-counter"
        >
          {notes.length > COUNTER_VISIBLE_FROM && (
            <span data-testid="assessment-letter-notes-count">
              {notes.length.toLocaleString()} of {ASSESSMENT_NOTES_MAX.toLocaleString()} characters ·{' '}
            </span>
          )}
          Paste freely, up to {ASSESSMENT_NOTES_MAX.toLocaleString()} characters. Signatures and
          boilerplate are stripped automatically.
        </p>
        {notesShort && (
          <p className="text-xs text-ink-soft mt-0.5" data-testid="assessment-letter-notes-hint">
            A bit more to work with helps. Aim for at least {NOTES_MIN} characters of notes
            ({notes.trim().length} so far).
          </p>
        )}
      </div>

      <div data-testid="assessment-letter-picks">
        <span className={LABEL_CLS}>Recommendations to include</span>
        {picks.length === 0 ? (
          <p className="mt-2 text-sm text-sage-400" data-testid="assessment-letter-picks-empty">
            The letter carries the recommendations your notes support; pick more from the library.
          </p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {picks.map((p) => (
              <li
                key={p.id}
                className="inline-flex flex-wrap items-center gap-1.5 rounded-pill bg-sand px-3 py-1 text-sm text-sage-800"
                data-testid="assessment-letter-pick"
              >
                <span>{p.topic}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemovePick(p.id)}
                  aria-label={`Remove ${p.topic}`}
                  className="!min-h-0 !px-1 !py-0"
                >
                  <X size={14} aria-hidden="true" />
                </Button>
                {isPickStale(p, currentPronounKey) && p.worded_with !== undefined && (
                  <span className="text-xs text-warm-700">
                    <span data-testid="recommendation-stale-note">
                      Worded with {pronounChoiceLabel(p.worded_with)}. Remove and add it again.
                    </span>{' '}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRetryPick(p.id)}
                      className="!min-h-0 !px-1 !py-0 underline"
                      data-testid="recommendation-stale-retry"
                    >
                      Try again
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {errorKey !== null && <ErrorNotice surface="assessment_letter_generate" code={errorKey} />}

      <Button
        type="submit"
        variant={hasDraft ? 'ghost' : 'primary'}
        size={hasDraft ? 'sm' : 'md'}
        disabled={!canSubmit}
        loading={generating}
        icon={<Sparkles size={17} aria-hidden="true" />}
      >
        {generating
          ? `Writing the letter. This usually takes a minute or two, feel free to set this aside and come back…${elapsed > 15 ? ` (${elapsed}s)` : ''}`
          : 'Write the letter'}
      </Button>
    </form>
  );
}
